import React, { useState, useRef, useEffect } from 'react';
import { SketchPicker, ColorResult } from 'react-color';
import ReactDOM from 'react-dom';

// TODO: Revisit Launchpad color-picker input UX and state synchronization in a dedicated follow-up pass.

interface ColorPickerPortalProps {
	isOpen: boolean;
	position: {
		top: number;
		left: number;
	};
	color: string;
	onChangeComplete?: (color: string) => void;
	onChange?: (color: string) => void;
	onClose: () => void;
}

// Create a component that renders an HTML element outside of React's hierarchy
const ColorPickerPortal: React.FC<ColorPickerPortalProps> = ({
	isOpen,
	position,
	color,
	onChangeComplete = () => {},
	onChange,
	onClose,
}) => {
	const [localColor, setLocalColor] = useState(color);
	const pickerRef = useRef<HTMLDivElement>(null);
	const wasOpenRef = useRef(false);

	useEffect(() => {
		const wasOpen = wasOpenRef.current;

		if (!isOpen || !wasOpen) {
			setLocalColor(color);
		}

		wasOpenRef.current = isOpen;
	}, [color, isOpen]);

	// Close picker on ESC key
	useEffect(() => {
		const handleKeyDown = (e: KeyboardEvent) => {
			if (e.key === 'Escape') {
				onClose();
			}
		};

		if (isOpen) {
			document.addEventListener('keydown', handleKeyDown);
		}

		return () => {
			document.removeEventListener('keydown', handleKeyDown);
		};
	}, [isOpen, onClose]);

	// Handle clicks outside the picker
	useEffect(() => {
		const handleClickOutside = (e: MouseEvent) => {
			if (pickerRef.current && !pickerRef.current.contains(e.target as Node)) {
				const target = e.target as HTMLElement;
				if (
					!target.closest(
						'.color-swatch-container, .color-swatch-click-target',
					)
				) {
					onClose();
				}
			}
		};

		if (isOpen) {
			// Add with a slight delay to avoid immediate trigger
			const timer = setTimeout(() => {
				document.addEventListener('click', handleClickOutside);
			}, 10);

			return () => {
				clearTimeout(timer);
				document.removeEventListener('click', handleClickOutside);
			};
		}
	}, [isOpen, onClose]);

	if (!isOpen) return null;

	return ReactDOM.createPortal(
		<div
			ref={pickerRef}
			style={{
				position: 'fixed',
				top: position.top,
				left: position.left,
				// eslint-disable-next-line no-restricted-syntax -- grandfathered: raw z-index fenced as step 2b, the portal band is a cross-repo decision. See docs/css-unification/overlay-and-zindex.md
				zIndex: 9999,
				backgroundColor: 'var(--bs-white)',
				borderRadius: 'var(--bs-border-radius)',
				boxShadow: 'var(--bs-box-shadow)',
			}}
			onClick={(e) => e.stopPropagation()}
		>
			<SketchPicker
				disableAlpha
				color={localColor}
				onChange={(nextColor: ColorResult) => {
					setLocalColor(nextColor.hex);
					// Call onChange for immediate feedback
					if (onChange) onChange(nextColor.hex);
				}}
				onChangeComplete={(color: ColorResult) => {
					onChangeComplete(color.hex);
				}}
			/>
		</div>,
		document.body,
	);
};

export default ColorPickerPortal;
